import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from '../../environments/environment';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

interface SSOStatusResponse {
  status: 'started' | 'fetching' | 'creating_org' | 'syncing_resources' | 'completed' | 'error';
  message: string;
  progress: number;
  token?: string;
  allunitedAccessToken?: string;
  organizationId?: number;
  organizationName?: string;
  error?: string;
}

@Component({
  selector: 'app-sso-callback',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatProgressBarModule
  ],
  templateUrl: './sso-callback.component.html',
  styleUrls: ['./sso-callback.component.scss']
})
export class SsoCallbackComponent implements OnInit, OnDestroy {
  status: string = 'starting';
  message: string = 'Initializing SSO authentication...';
  progress: number = 0;
  error: string | null = null;

  private sessionId: string | null = null;
  private pollSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    // Get ssostring from query params
    this.route.queryParams.subscribe(params => {
      const ssostring = params['ssostring'];

      if (!ssostring) {
        this.error = 'Missing SSO string parameter';
        return;
      }

      this.startSSOLogin(ssostring);
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Start SSO login flow
   */
  private startSSOLogin(ssostring: string): void {
    this.message = 'Connecting to AllUnited backoffice...';

    this.http.post<{ sessionId: string; status: string }>(
      `${environment.apiBaseUrl}/api/auth/sso/start`,
      { ssostring }
    ).subscribe({
      next: (response) => {
        this.sessionId = response.sessionId;
        this.startPolling();
      },
      error: (err) => {
        this.error = err.error?.error || 'Failed to start SSO authentication';
        this.status = 'error';
      }
    });
  }

  /**
   * Start polling for SSO status updates
   */
  private startPolling(): void {
    if (!this.sessionId) return;

    this.pollSubscription = interval(500)
      .pipe(
        switchMap(() =>
          this.http.get<SSOStatusResponse>(
            `${environment.apiBaseUrl}/api/auth/sso/status/${this.sessionId}`
          )
        ),
        takeWhile((response) => response.status !== 'completed' && response.status !== 'error', true)
      )
      .subscribe({
        next: (response) => {
          this.status = response.status;
          this.message = response.message;
          this.progress = response.progress;

          if (response.status === 'completed' && response.token && response.organizationId) {
            this.handleSSOComplete(response.token, response.organizationId, response.organizationName || 'Organization', response.allunitedAccessToken);
          } else if (response.status === 'error') {
            this.error = response.error || 'SSO authentication failed';
          }
        },
        error: (err) => {
          this.error = err.error?.error || 'Failed to check SSO status';
          this.status = 'error';
          this.stopPolling();
        }
      });
  }

  /**
   * Stop polling
   */
  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  /**
   * Handle successful SSO completion
   */
  private handleSSOComplete(token: string, organizationId: number, organizationName: string, allunitedAccessToken?: string): void {
    // Store auth token
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_username', 'AllUnited User');

    // Store AllUnited access token for API calls
    if (allunitedAccessToken) {
      localStorage.setItem('allunited_access_token', allunitedAccessToken);
    }

    // Store selected organization
    localStorage.setItem('selectedOrganisationId', organizationId.toString());
    localStorage.setItem('selectedOrganisationName', organizationName);

    // Stop polling
    this.stopPolling();

    // Redirect to dashboard after a short delay
    setTimeout(() => {
      this.router.navigate(['/dashboard']);
    }, 1000);
  }

}
